import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback
} from 'react';

import { client } from '../sanityProject/sanityClient';
import { blogPostsQuery } from '../sanityProject/sanityData';

const BlogContext = createContext();

const CACHE_KEY = 'blogCache';
const CACHE_TIME_KEY = 'blogCacheTime';
const CACHE_DURATION = 60 * 60 * 1000; // 60 minuten

const getCachedPosts = () => {
  if (typeof window === 'undefined') return [];

  try {
    const cached = localStorage.getItem(CACHE_KEY);

    return cached ? JSON.parse(cached) : [];
  } catch (error) {
    console.error('Error parsing cached posts:', error);
    return [];
  }
};

const isCacheValid = () => {
  if (typeof window === 'undefined') return false;

  try {
    const cached = localStorage.getItem(CACHE_KEY);
    const cachedTime = localStorage.getItem(CACHE_TIME_KEY);

    if (!cached || !cachedTime) return false;

    const age = Date.now() - parseInt(cachedTime, 10);

    return !isNaN(age) && age < CACHE_DURATION;
  } catch {
    return false;
  }
};

export const BlogProvider = ({ children }) => {
  const [posts, setPosts] = useState(getCachedPosts);
  const [loading, setLoading] = useState(!isCacheValid());

  const updatePosts = useCallback((newPosts) => {
    setPosts(newPosts);

    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(newPosts));
        localStorage.setItem(CACHE_TIME_KEY, Date.now().toString());
      } catch (error) {
        console.error('Error saving posts to cache:', error);
      }
    }
  }, []);

  const fetchPosts = useCallback(async () => {
    setLoading(true);

    try {
      const data = await client.fetch(blogPostsQuery);

      if (Array.isArray(data)) {
        updatePosts(data);
      } else {
        console.warn('Fetched blog data is not an array');
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  }, [updatePosts]);

  useEffect(() => {
    if (!isCacheValid()) {
      fetchPosts();
    } else {
      setLoading(false);
    }

    const handleStorageChange = (e) => {
      if (e.key === CACHE_KEY && e.newValue) {
        try {
          const updatedPosts = JSON.parse(e.newValue);

          if (Array.isArray(updatedPosts)) {
            setPosts(updatedPosts);
          }
        } catch (error) {
          console.error('Error syncing storage:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [fetchPosts]);

  return (
    <BlogContext.Provider
      value={{
        posts,
        loading,
        fetchPosts,
        updatePosts
      }}
    >
      {children}
    </BlogContext.Provider>
  );
};

export const useBlog = () => {
  const context = useContext(BlogContext);

  if (!context) {
    throw new Error('useBlog must be used within a BlogProvider');
  }

  return context;
};